/**
 * FlauschPalast Auto-Angebotscode v0.1.0 / QA only.
 * Wird ausschließlich durch aktiven Klick auf das Kopiersymbol vorgemerkt.
 * Nutzt Shopify Cart Ajax API, keine Checkout-Mutation und keinen externen Dienst.
 */
(() => {
  'use strict';
  if (window.FPDiscountAutoCart) return;

  const KEY = 'fp_copied_discount_pending_v1';
  const TTL = 24 * 60 * 60 * 1000;
  let memory = null;
  let busy = null;
  let needsRecheck = false;

  const normalize = value => {
    if (typeof value !== 'string') return '';
    const code = value.trim();
    return code && code.length <= 100 && !/[,<>\r\n]/.test(code) ? code : '';
  };
  const equal = (a, b) =>
    String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  const cents = value => {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
  };
  const clear = expected => {
    if (expected && memory && !equal(expected, memory.code)) return;
    memory = null;
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
      if (!expected || !stored || equal(expected, stored.code)) {
        window.sessionStorage.removeItem(KEY);
      }
    } catch (_) {}
  };
  const pending = () => {
    let item = memory;
    if (!item) {
      try { item = JSON.parse(window.sessionStorage.getItem(KEY) || 'null'); }
      catch (_) { return null; }
    }
    if (!item) return null;
    const age = Date.now() - Number(item.savedAt);
    if (!normalize(item.code) || !Number.isFinite(age) ||
        age < -60000 || age > TTL) {
      clear(item.code);
      return null;
    }
    return item;
  };
  const store = item => {
    memory = item;
    try {
      window.sessionStorage.setItem(KEY, JSON.stringify(item));
      return true;
    } catch (_) { return false; }
  };
  const root = () => {
    const route = window.Shopify?.routes?.root || '/';
    return route.endsWith('/') ? route : route + '/';
  };
  const readCart = async () => {
    const response = await window.fetch(root() + 'cart.js', {
      method: 'GET',
      headers: {Accept:'application/json'},
      cache: 'no-store',
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('cart_read_failed');
    return response.json();
  };
  const discountName = item =>
    typeof item === 'string'
      ? item.trim()
      : String(item?.code || item?.title || '').trim();
  const assess = (cart, code) => {
    const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
    const applications = Array.isArray(cart?.discount_applications) ?
      cart.discount_applications : [];
    const codeActive = codes.some(item =>
      equal(discountName(item), code) && item.applicable === true);
    const hasApplication = applications.some(item => equal(discountName(item), code));
    const applied = hasApplication ||
      (codeActive && Number(cart?.total_discount || 0) > 0);
    const conflict = codes.some(item =>
      discountName(item) && !equal(discountName(item), code)) ||
      applications.some(item => !equal(discountName(item), code));
    const total = Number(cart?.items_subtotal_price ??
      cart?.original_total_price ?? cart?.total_price ?? 0);
    const count = Number(cart?.item_count ?? cart?.items?.length ?? 0);
    return {
      applied, conflict,
      subtotal: Number.isFinite(total) ? Math.max(total, 0) : 0,
      hasItems: count > 0
    };
  };
  const say = (message, kind) => {
    try {
      if (typeof webvista !== 'undefined' &&
          typeof webvista.popToast === 'function') {
        webvista.popToast(message, kind || 'success');
      }
    } catch (_) {}
  };
  const minText = value => new Intl.NumberFormat(
    document.documentElement.lang || 'de-DE',
    {style:'currency',currency:'EUR'}
  ).format(value / 100);

  const sections = () => {
    const targets = [];
    const drawer = document.getElementById('Cart-Drawer');
    const cart = document.getElementById('Main-Cart');
    const bubble = document.getElementById('Cart-Icon-Bubble');
    if (drawer?.dataset.section && !drawer.hasAttribute('data-status-silence')) {
      targets.push({
        id:'Cart-Drawer', section:drawer.dataset.section,
        selector:'#Cart-Drawer-Details'
      });
    }
    if (cart?.dataset.section) {
      targets.push({
        id:'Main-Cart', section:cart.dataset.section,
        selector:'#Main-Cart-Details'
      });
    }
    if (bubble) {
      targets.push({
        id:'Cart-Icon-Bubble', section:'cart-icon-bubble',
        selector:'.shopify-section'
      });
    }
    return targets;
  };
  const apply = async offer => {
    const targets = sections();
    const response = await window.fetch(
      window.routes?.cart_update_url || root() + 'cart/update.js',
      {
        method:'POST',
        credentials:'same-origin',
        headers:{'Content-Type':'application/json',Accept:'application/json'},
        body:JSON.stringify({
          discount:offer.code,
          sections:[...new Set(targets.map(x => x.section))],
          sections_url:window.location.pathname
        })
      }
    );
    if (!response.ok) throw new Error('cart_update_failed');
    const data = await response.json();
    if (!data || data.error || data.status === 422) {
      throw new Error('discount_failed');
    }
    return {data,targets};
  };
  const updateView = result => {
    if (!result.data?.sections || typeof SectionDynamicUpdate === 'undefined') return;
    const targets = result.targets.filter(item =>
      typeof result.data.sections[item.section] === 'string');
    if (!targets.length) return;
    try {
      SectionDynamicUpdate.updateSections(targets, result.data.sections);
    } catch (_) {
      // Never undo an accepted code if a section cannot be refreshed.
    }
  };
  const perform = async origin => {
    const offer = pending();
    if (!offer) return {status:'none'};
    try {
      const cart = await readCart();
      const state = assess(cart, offer.code);
      if (state.applied) {
        clear(offer.code);
        return {status:'applied',already:true};
      }
      if (state.conflict) {
        clear(offer.code);
        if (origin === 'click') {
          say('Anderer Rabatt bereits aktiv – der Code wurde nur kopiert.', 'warning');
        }
        return {status:'conflict'};
      }
      if (!state.hasItems || state.subtotal < offer.minimumCents) {
        if (origin === 'click') {
          say('Code vorgemerkt – wird ab ' + minText(offer.minimumCents) +
            ' automatisch angewendet.', 'success');
        }
        return {status:'queued'};
      }
      if (!equal(pending()?.code, offer.code)) return {status:'superseded'};
      const result = await apply(offer);
      let verified = assess(result.data, offer.code);
      if (!verified.applied) verified = assess(await readCart(), offer.code);
      if (!verified.applied) {
        clear(offer.code);
        if (origin === 'click' || origin === 'event') {
          say('Der Angebotscode ist für diesen Warenkorb nicht anwendbar.', 'warning');
        }
        return {status:'rejected'};
      }
      updateView(result);
      clear(offer.code);
      if (origin === 'click' || origin === 'event') {
        say('Angebotscode im Warenkorb automatisch angewendet.', 'success');
      }
      return {status:'applied',already:false};
    } catch (_) {
      if (origin === 'click') {
        say('Code kopiert. Die automatische Einlösung wird erneut geprüft.', 'warning');
      }
      return {status:'retry'};
    }
  };
  const check = origin => {
    if (busy) {
      needsRecheck = true;
      return busy;
    }
    const task = perform(origin || 'background');
    busy = task;
    void task.then(() => {
      busy = null;
      if (needsRecheck) {
        needsRecheck = false;
        if (pending()) void check('event');
      }
    });
    return task;
  };
  const activate = (code, minimumCents) => {
    const normalized = normalize(code);
    if (!normalized) return Promise.resolve({status:'invalid-code'});
    const persisted = store({
      code:normalized,
      minimumCents:cents(minimumCents),
      savedAt:Date.now()
    });
    return check('click').then(result => ({...result,persisted}));
  };
  const init = () => {
    if (typeof webvista !== 'undefined' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        typeof webvista.subscribe === 'function') {
      webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (pending()) return check('event');
      });
    }
    window.addEventListener('pageshow', () => {
      if (pending()) void check('background');
    });
    if (pending()) void check('background');
  };
  window.FPDiscountAutoCart = {activate};
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once:true});
  } else { init(); }
})();